import { useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';

// Routes
import { CONTENT_ALBUM_ANALYTICS } from '@/routes/privateRoutes';

//libs
import { PINTEREST, TWITTER } from '@/lib/socialProfile';
import { ALBUM, ALBUMS } from '@/lib/routes/apiEndpoints';

// Utils
import { post, put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Hooks
import { useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';

// Components
import Modal from '@/components/base/Modal';
import InfoBanner from '@/components/base/InfoBanner';
import FooterModal from '@/components/pages/albums/AlbumFunnelModal/components/FooterModal';
import InputAlbumName from '@/components/pages/albums/AlbumFunnelModal/components/InputAlbumName';
import InstagramMediaType from '@/components/pages/albums/AlbumFunnelModal/components/InstagramMediaType';
import PostListWrapper from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/PostListWrapper';
import AlbumManualFilters from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/AlbumManualFilters';
import StoriesListWrapper from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/StoriesListWrapper';

export const ManualStep = ({ dismiss, callbackUpdateAlbum }) => {
    const router = useRouter();
    const { t } = useTranslation('content');
    const refBannerInfo = useRef();
    const bannerInfoInViewPort = useIntersectionObserver(refBannerInfo, '0px');
    const [submitStatus, setSubmitStatus] = useState({
        isSubmitting: false,
        error: null
    });

    const { albumInfo } = useAlbumContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const network = activeSocialProfile?.social_network_type;

    const submitManualAlbum = async () => {
        setSubmitStatus((prevState) => ({
            ...prevState,
            isSubmitting: true,
            error: null
        }));
        try {
            const dataToSend = {
                name: albumInfo?.name,
                type: albumInfo?.type,
                medias: albumInfo?.medias,
                criteria: albumInfo?.criteria?.album_type
                    ? {
                          album_type: albumInfo?.criteria?.album_type
                      }
                    : undefined
            };
            if (albumInfo?.id) {
                await put(
                    injectEndpointUrlParams(ALBUM, {
                        SPID: activeSocialProfile?.id,
                        ALBUM_ID: albumInfo?.id
                    }),
                    dataToSend
                );

                if (typeof callbackUpdateAlbum === 'function') {
                    await callbackUpdateAlbum();
                }

                if (typeof dismiss === 'function') {
                    dismiss();
                }
            } else {
                const { data } = await post(
                    ALBUMS.replace('%SPID%', activeSocialProfile?.id),
                    dataToSend
                );
                router.push(CONTENT_ALBUM_ANALYTICS.replace('[albumId]', data?.data?.id));
            }
        } catch (e) {
            if (e?.response?.data?.errors?.name?.includes('unique')) {
                setSubmitStatus((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    error: t('content:modal.update_album_error_name_unique')
                }));
            } else {
                setSubmitStatus((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    error: t('errors:error_occured_submit_data')
                }));
            }

            // Scroll To info banner if it's not visible in the viewport
            if (!bannerInfoInViewPort) {
                refBannerInfo?.current?.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    const getModalTitle = () => {
        switch (network) {
            case PINTEREST:
                return t('modal.create_modal.title_create_man_pinterest');
            case TWITTER:
                return t('modal.create_modal.title_create_man_twitter');
            default:
                return t('modal.create_modal.title_create_man');
        }
    };

    return (
        <div className="flex max-h-[calc(100vh-100px)] min-h-[500px] w-[calc(100vw-24px)] flex-col sm:max-w-[62.5rem]">
            <Modal.Body className="flex flex-1 flex-col overflow-hidden px-5 pt-12 sm:px-12">
                <div ref={refBannerInfo} />
                {submitStatus?.error ? (
                    <InfoBanner
                        className="mb-4"
                        title={t('errors:error')}
                        message={submitStatus?.error}
                        onClose={() => {
                            setSubmitStatus((prevState) => ({
                                ...prevState,
                                error: null
                            }));
                        }}
                    />
                ) : null}
                <Modal.Title className="mb-8 text-center">{getModalTitle()}</Modal.Title>
                {albumInfo?.id ? (
                    <div className="mb-6">
                        <InputAlbumName />
                    </div>
                ) : null}
                <Modal.Content className="scrollbar flex-1 rounded-md bg-icoGray-100 dark:bg-icoDarkMode-deepBlack">
                    <InstagramMediaType />
                    <div className="px-3 pt-5 sm:px-6 sm:pt-8">
                        <AlbumManualFilters />
                        <div className="space-y-3">
                            {albumInfo?.criteria?.album_type === 'stories' ? (
                                <StoriesListWrapper />
                            ) : (
                                <PostListWrapper />
                            )}
                        </div>
                    </div>
                </Modal.Content>
            </Modal.Body>
            <FooterModal
                disableSubmitButton={!albumInfo?.medias?.length}
                submitStatus={submitStatus}
                submitAlbum={submitManualAlbum}
            />
        </div>
    );
};
